"use client"

import {
  Stack,
  Box,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Input,
  Textarea,
  FormLabel,
  Select,
  Button,
  Spinner,
  IconButton,
} from "@chakra-ui/react"
import "services/Recruit/assets/css/CreateJobOpening/style.css"
import { ArrowBackIcon, CloseIcon, DownloadIcon } from "@chakra-ui/icons"
import { globalStyles } from "common/theme/styles"
import { AttachmentIcon } from "services/Recruit/assets/svg/AttachmentIcon"
import { Field, Formik, Form } from "formik"
import { useEffect, useState, useMemo, useCallback } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { constant } from "common/constant"
import FileUpload from "common/components/fileUpload/FileUpload"
import ActionNavbar from "common/components/actionBar/ActionNavbar"
import { useDispatch, useSelector } from "react-redux"
import { uploadAttachment } from "common/redux/reducers/attachmentSlice"
import { APPLICATION_INITIAL_VALUES } from "common/initialValues/formInitialValues"
import { toast } from "react-toastify"
import { getApi } from "common/services/api"
import { API_PATHS } from "common/services/apiPaths"
import { putApi } from "common/services/api"
import { ApplicationSchema } from "services/Recruit/schema/applicationSchema"
import { applicationTabsData } from "services/Recruit/data/tabsData"
import { recruitConstant } from "services/Recruit/constant"
import { downloadAttachment } from "common/redux/reducers/candidateSlice"
import * as Yup from "yup"

import ReactSelect from "react-select"
import { addApplication } from "common/redux/reducers/applicationSlice"
import { applicationStatusOptions, manualApplicationStatusOptions } from "services/Recruit/data/dropdownData"
import { HasAccess } from "common/utils/accessUtils"
import { accessModuleTitles } from "common/constant"
import { validateS3File } from "services/Crm/utils/calcUtils"
import { generateS3Path } from "services/Crm/utils/calcUtils"
import S3Helper from "common/utils/s3-helper"
import { ACCEPTED_DOCUMENT_TYPES } from "services/Crm/utils/calcUtils"
import axios from "axios"
import { removeTimestamp } from "services/Crm/utils/calcUtils"
import { extractS3KeyFlexible } from "services/Crm/utils/calcUtils"
import { MAX_ATTACHMENT_FILE_SIZE } from "services/Crm/utils/calcUtils"
import { MAX_ATTACHMENT_FILE_SIZE_MB } from "services/Crm/utils/calcUtils"
import { isFormDirty } from "common/utils/formUtils"
import { formatNoticePeriod } from "common/utils/textFormatters"
import Interview from "../interview"
import DocumentVerificationTab from "services/Recruit/components/DocumentVerification/DocumentVerificationTab";
import LOITab from "services/Recruit/components/LOI/LOITab";

// Statuses that allow access to Doc Verification and LOI tabs
const ALLOWED_STATUSES_FOR_DOC_LOI = [
  'interview_cleared',
  'document_verification_in_progress',
  'document_verification_completed',
  'loi_in_progress',
  'loi_completed',
  'loi_rejected_by_candidate',
  'loi_expired',
  'offered',
  'hired'
];

// Statuses where LOI is completed/final - show all green ticks
const LOI_COMPLETED_STATUSES = [
  'loi_completed',
  'offered',
  'hired'
];

const REMARKS_PLACEHOLDER_BY_STATUS = {
  submitted: "Add any initial notes for this application status",
  in_review: "Add review-related remarks",
  shortlisted: "Add notes for shortlist decision",
  interview_scheduled: "Add interview scheduling remarks",
  interview_cleared: "Add remarks after interview clearance",
  rejected: "Add rejection reason or context",
  offered: "Add offer-related remarks",
  hired: "Add joining or hiring remarks",
};

const OverviewPanel = ({
  navigate,
  isOverviewMode,
  id,
  applicationData,
  handleDownloadAttachment,
  pendingUploads,
  removedFiles,
  permission,
  colors,
  activeTabIndex,
  getTabStates,
  handleTabChange,
  handleNext,
  handleBack,
  editLoading,
  isEditMode,
  originalApplicationData,
  jobData,
  candidateData,
  postingTitleValue,
  selectedCandidate,
  selectedJob,
  EditData,
  dispatch,
  setSelectedJob,
  setSelectedCandidate,
  visibleTabs,
  handleBackNavigation,
}) => {
  return (
    <>
      <Box className="createJobOpening-container">
        <Box className="createJobOpening-mainContainer">
          <Box className="createJobOpening-breadCrumb">
            <ArrowBackIcon onClick={handleBackNavigation} fontSize="20px" cursor={"pointer"} />
            <Text className="createJobOpening-pageTitle">
              {isOverviewMode ? "Overview" : id ? constant.editJobApplication : constant.createApplication}
            </Text>
          </Box>
        </Box>

        <Formik
          enableReinitialize
          initialValues={
            applicationData
              ? {
                postingTitle: postingTitleValue(),
                name: applicationData?.candidateDetails?._id,
                email: applicationData?.candidateDetails?.email,
                phone: applicationData?.candidateDetails?.phone,
                city: applicationData?.candidateDetails?.city,
                experienceYears: applicationData?.candidateDetails?.experienceYears,
                state: applicationData?.candidateDetails?.state,
                noticePeriod: applicationData?.candidateDetails?.noticePeriod,
                status: applicationData?.status,
                remarks: applicationData?.remarks || "",
                applicationSource: applicationData?.candidateDetails?.source,
                resume: applicationData?.candidateDetails?.resume,
                coverLetter: applicationData?.candidateDetails?.coverLetter,
              }
              : APPLICATION_INITIAL_VALUES
          }
          validationSchema={ApplicationSchema}
          onSubmit={(values, { resetForm }) => {
            const finalValues = {
              candidateDetails: selectedCandidate?._id,
              job: selectedJob?._id,
              status: values.status,
              remarks: values.remarks || "",
            }
            if (id) {
              EditData(finalValues)
            } else {
              dispatch(addApplication({ values: finalValues, resetForm, navigate }))
              toast.success("Application created successfully")
              navigate("/recruitment/applications")
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit, errors, dirty, isValid, setErrors, setTouched }) => {
            const tabStates = getTabStates(values, dirty, isValid, activeTabIndex)
            const isDirty = isFormDirty(originalApplicationData || APPLICATION_INITIAL_VALUES, values);

            // Check if LOI is completed/final stage
            const isLOICompleted = LOI_COMPLETED_STATUSES.includes(values.status);

            // Check if Doc Verification and LOI tabs should be enabled based on application status
            const isDocAndLOITabsEnabled = id
              ? ALLOWED_STATUSES_FOR_DOC_LOI.includes(applicationData?.status)
              : ALLOWED_STATUSES_FOR_DOC_LOI.includes(values.status);

            return (
              <Form onSubmit={handleSubmit}>
                <Tabs
                  index={activeTabIndex}
                  onChange={(index) => handleTabChange(index, values, dirty, isValid)}
                  className="createJobOpening-tabsContainer"
                  variant="unstyled"
                  orientation="vertical"
                >
                  <TabList w={"40%"} className="createJobOpening-tabListContainer">
                    {visibleTabs.map(({ label, Icon }, index) => {
                      const originalIndex = applicationTabsData.findIndex(tab => tab.label === label);
                      const tabState = tabStates[originalIndex];
                      const isCurrentTab = originalIndex === activeTabIndex;

                      // For tabs 2 and 3, check if they should be disabled based on status
                      const isRestrictedTab = originalIndex >= 2;
                      const isTabDisabled = isRestrictedTab
                        ? !isDocAndLOITabsEnabled || !tabState.isAccessible
                        : !tabState.isAccessible;

                      return (
                        <Tab
                          className="createJobOpening-tabs"
                          key={originalIndex}
                          _selected={{
                            color: colors.cta["100"],
                            borderLeft: "2px solid",
                            borderColor: colors.cta["100"],
                          }}
                          _hover={{
                            color: !isTabDisabled ? colors.cta["100"] : "inherit",
                            cursor: !isTabDisabled ? "pointer" : "not-allowed",
                          }}
                          _focus={{ boxShadow: "none" }}
                          display="flex"
                          alignItems="center"
                          gap="8px"
                          opacity={!isTabDisabled ? 1 : 0.6}
                          cursor={!isTabDisabled ? "pointer" : "not-allowed"}
                          isDisabled={isTabDisabled}
                        >
                          <Icon size={20} />
                          {label}
                          {(isLOICompleted || tabState.isValid) && (
                            <Text fontSize="xs" color="green.500">
                              ✓
                            </Text>
                          )}
                          {/* Show lock icon for restricted tabs when not enabled */}
                          {isRestrictedTab && !isDocAndLOITabsEnabled && (
                            <Text fontSize="xs" color="gray.400" title="Available when status is Interview Cleared or later">
                              🔒
                            </Text>
                          )}
                        </Tab>
                      )
                    })}
                  </TabList>

                  <TabPanels w={"60%"} className="createJobOpening-tabsPanelContainer">
                    {visibleTabs.map(({ label }, visibleIndex) => {
                      const originalIndex = applicationTabsData.findIndex(tab => tab.label === label);

                      if (originalIndex === 0) {
                        // Job Opening and Candidate Info Tab
                        return (
                          <TabPanel key={`tab-${originalIndex}`} className="createJobOpening-tabsPanel">
                            <Box className="createJobOpening-tabsPanelBody">
                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="postingTitle">
                                    {constant.postingTitle}
                                    <span className="astrick">*</span>
                                  </FormLabel>
                                  <Field name="postingTitle">
                                    {({ field, meta, form }) => {
                                      const postingTitleOptions =
                                        jobData && jobData.length > 0
                                          ? jobData.map((option) => {
                                            const formattedTitle = option.postingTitle
                                              ?.split("-")
                                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                              .join(" ")
                                            const jobId = option.customId
                                            return {
                                              value: `${formattedTitle} - ${jobId}`,
                                              label: `${formattedTitle.replace('Other ', '')} - ${jobId}`,
                                              id: option?._id,
                                            }
                                          })
                                          : [{ value: "", label: "No data found", isDisabled: true }]
                                      const selectedOption =
                                        (postingTitleOptions &&
                                          postingTitleOptions.length > 0 &&
                                          postingTitleOptions.find((option) => option?.value === field?.value)) ||
                                        null
                                      return (
                                        <>
                                          <ReactSelect
                                            isDisabled={!permission?.update || isOverviewMode}
                                            name={field.name}
                                            value={selectedOption}
                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            options={postingTitleOptions}
                                            className="basic-single-select reactSelect"
                                            classNamePrefix="select"
                                            onChange={(selectedOption) => {
                                              const selectedValue = selectedOption ? selectedOption?.value : ""
                                              form.setFieldValue(field.name, selectedValue)
                                              const selectedId = selectedOption ? selectedOption?.id : null
                                              const selectedJob = jobData.find((job) => job?._id === selectedId)
                                              setSelectedJob(selectedJob || {})
                                            }}
                                            placeholder="Select Job Title"
                                            isClearable
                                            isSearchable
                                            styles={{
                                              control: (provided) => ({
                                                ...provided,
                                                borderColor:
                                                  meta.touched && meta.error ? colors.errorText : colors.borderColor,
                                              }),
                                            }}
                                          />
                                          {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                        </>
                                      )
                                    }}
                                  </Field>
                                </Box>

                                <Box w={"50%"}>
                                  <FormLabel htmlFor="name">
                                    {recruitConstant.name}
                                    <span className="astrick">*</span>
                                  </FormLabel>
                                  <Field name="name">
                                    {({ field, meta, form }) => {
                                      const candidateNameOptions =
                                        candidateData && candidateData.length > 0
                                          ? candidateData?.map((option) => ({
                                            value: option._id,
                                            label: option.name,
                                            id: option._id,
                                          }))
                                          : [{ value: "", label: "No data found", isDisabled: true }]
                                      const selectedOption =
                                        (candidateNameOptions &&
                                          candidateNameOptions.length > 0 &&
                                          candidateNameOptions.find((option) => option?.value === field?.value)) ||
                                        null

                                      const selectedCandidateDetails =
                                        candidateData &&
                                        candidateData.length > 0 &&
                                        candidateData?.find((candidate) => candidate._id === selectedOption?.value)
                                      setSelectedCandidate(selectedCandidateDetails)
                                      return (
                                        <>
                                          <ReactSelect
                                            isDisabled={!permission?.update || isOverviewMode}
                                            name={field.name}
                                            value={selectedOption}
                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            options={candidateNameOptions}
                                            className="basic-single-select reactSelect"
                                            classNamePrefix="select"
                                            onChange={(selectedOption) => {
                                              const selectedValue = selectedOption ? selectedOption.value : ""
                                              form.setFieldValue(field.name, selectedValue)
                                              const selectedCandidate = candidateData.find(
                                                (candidate) => candidate._id === selectedValue,
                                              )
                                              setSelectedCandidate(selectedCandidate)

                                              if (selectedCandidate) {
                                                form.setFieldValue("phone", selectedCandidate.phone || "")
                                                form.setFieldValue("email", selectedCandidate.email || "")
                                                form.setFieldValue("city", selectedCandidate.city || "")
                                                form.setFieldValue("state", selectedCandidate.state || "")
                                                form.setFieldValue("experienceYears", selectedCandidate.experienceYears || "")
                                                form.setFieldValue("noticePeriod", selectedCandidate.noticePeriod || "")
                                                form.setFieldValue("applicationSource", selectedCandidate.source || "")
                                                form.setFieldValue("resume", selectedCandidate.resume || "")
                                                form.setFieldValue("coverLetter", selectedCandidate.coverLetter || "")
                                              } else {
                                                form.setFieldValue("phone", "")
                                                form.setFieldValue("email", "")
                                              }
                                            }}
                                            placeholder="Select Candidate Name"
                                            isClearable
                                            isSearchable
                                            styles={{
                                              control: (provided) => ({
                                                ...provided,
                                                borderColor:
                                                  meta.touched && meta.error ? colors.errorText : colors.borderColor,
                                              }),
                                            }}
                                          />
                                          {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                        </>
                                      )
                                    }}
                                  </Field>
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="email">
                                    {recruitConstant.email} <span className="astrick">*</span>
                                  </FormLabel>
                                  <Input
                                    name="email"
                                    value={selectedCandidate?.email || ""}
                                    disabled
                                    placeholder={recruitConstant.emailPlaceHolderText}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="phone">
                                    {recruitConstant.phone}<span className="astrick">*</span>
                                  </FormLabel>
                                  <Input
                                    name="phone"
                                    value={selectedCandidate?.phone || ""}
                                    disabled
                                    placeholder={recruitConstant.phonePlaceHolderText}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="city">
                                    {recruitConstant.city}
                                  </FormLabel>
                                  <Input
                                    name="city"
                                    value={selectedCandidate?.city || ""}
                                    disabled
                                    placeholder={recruitConstant.cityPlaceHolderText}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="experienceYears">
                                    {recruitConstant.totalExperience}<span className="astrick">*</span>
                                  </FormLabel>
                                  <Input
                                    name="experienceYears"
                                    value={selectedCandidate?.experienceRange ?? ""}
                                    disabled
                                    placeholder={recruitConstant.selectTotalExp}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="state">
                                    {recruitConstant.state}
                                  </FormLabel>
                                  <Input
                                    name="state"
                                    value={selectedCandidate?.state || ""}
                                    disabled
                                    placeholder={recruitConstant.statePlaceHolderText}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="noticePeriod">
                                    {recruitConstant.noticePeriod}
                                  </FormLabel>
                                  <Input
                                    name="noticePeriod"
                                    value={formatNoticePeriod(selectedCandidate?.noticePeriod) || ""}
                                    disabled
                                    placeholder={recruitConstant.enterNoticePerios}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="status">
                                    {recruitConstant.status}<span className="astrick">*</span>
                                  </FormLabel>
                                  <Field name="status">
                                    {({ field, meta }) => (
                                      <>
                                        <Select
                                          isDisabled={!permission?.update || isOverviewMode}
                                          value={field.value || ""}
                                          {...field}
                                          borderColor={meta.touched && meta.error ? constant.errorText : constant.borderColor}
                                        >
                                          <option hidden value="">{constant.selectStatus}</option>
                                          {(
                                            [
                                              "document_verification_in_progress",
                                              "document_verification_completed",
                                              "loi_in_progress",
                                              "loi_completed",
                                              "loi_rejected_by_candidate",
                                              "loi_expired"
                                            ].includes(field.value)
                                              ? applicationStatusOptions
                                              : manualApplicationStatusOptions
                                          ).map(({ value, label, disabled, hidden }, index) => (
                                            <option key={index} value={value} disabled={disabled} hidden={hidden}>
                                              {constant[label] || label}
                                            </option>
                                          ))}
                                        </Select>
                                        {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="applicationSource">
                                    {recruitConstant.applicationSource}<span className="astrick">*</span>
                                  </FormLabel>
                                  <Input
                                    name="applicationSource"
                                    value={selectedCandidate?.source || ""}
                                    disabled
                                    placeholder={constant.selectApplicationSource}
                                    borderColor={colors.borderColor}
                                  />
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"50%"}>
                                  <FormLabel htmlFor="remarks">
                                    Remarks
                                  </FormLabel>
                                  <Field name="remarks">
                                    {({ field, meta }) => (
                                      <>
                                        <Textarea
                                          isDisabled={!permission?.update || isOverviewMode}
                                          {...field}
                                          value={field.value || ""}
                                          placeholder={
                                            REMARKS_PLACEHOLDER_BY_STATUS[values.status] ||
                                            "Add additional remarks based on selected status"
                                          }
                                          maxLength={1000}
                                          minH="80px"
                                          resize="vertical"
                                          borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>{meta.error}</Text>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                                <Box w={"50%"} />
                              </Flex>
                            </Box>
                            <Box className="createJobOpening-tabsPanelFooter">
                              <Button
                                className="theme-btn2 cancelBtn"
                                sx={{ textTransform: "capitalize" }}
                                variant="outline"
                                colorScheme={colors.error["100"]}
                                size="sm"
                                onClick={handleBackNavigation}
                              >
                                {recruitConstant.cancel}
                              </Button>
                              <Button
                                disabled={!tabStates[activeTabIndex]?.canProceed}
                                className="nextBtn"
                                variant="primary"
                                onClick={() => handleNext(values, dirty, isValid, setErrors, setTouched)}
                              >
                                {constant.next}
                              </Button>
                            </Box>
                          </TabPanel>
                        );
                      } else if (originalIndex === 1) {
                        // Attachments Tab
                        return (
                          <TabPanel key={`tab-${originalIndex}`} className="createJobOpening-tabsPanel">
                            <Box className="createJobOpening-tabsPanelBody">
                              <Flex gap={"20px"}>
                                <Box w={"100%"}>
                                  <Field name="resume">
                                    {({ field, form, meta }) => {
                                      const hasPendingFile = pendingUploads.resume
                                      const hasExistingFile = id && applicationData?.candidateDetails?.resume && !hasPendingFile && !removedFiles.resume
                                      const filename = hasExistingFile
                                        ? applicationData?.candidateDetails?.resume?.split("/")?.pop()
                                        : hasPendingFile
                                          ? pendingUploads.resume.originalName
                                          : selectedCandidate?.resume?.split("/")?.pop() || null
                                      return (
                                        <Flex align="center" gap={4}>
                                          <Box flex="1">
                                            <FileUpload
                                              label={constant.resume}
                                              icon={AttachmentIcon}
                                              selectedFile={
                                                selectedCandidate?.resume
                                                  ? `Selected: ${removeTimestamp(selectedCandidate.resume.split("/").pop())}`
                                                  : "No file"
                                              }
                                              title={constant.attachment}
                                              inputId="resume-upload"
                                              isDisabled
                                              errorText=""
                                              borderColor={colors.borderColor}
                                              accept="audio/*,video/*,image/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                            />
                                          </Box>
                                          {selectedCandidate?.resume && (
                                            <Flex gap={2}>
                                              <Button
                                                size="sm"
                                                leftIcon={<DownloadIcon />}
                                                onClick={() => {
                                                  handleDownloadAttachment(selectedCandidate.resume,
                                                    selectedCandidate.resume.split("/").pop());
                                                }}
                                                variant="outline"
                                              >
                                                Download
                                              </Button>
                                            </Flex>
                                          )}
                                        </Flex>
                                      );
                                    }}
                                  </Field>
                                </Box>
                              </Flex>

                              <Flex gap={"20px"}>
                                <Box w={"100%"}>
                                  <Field name="coverLetter">
                                    {({ field, form, meta }) => {
                                      const hasPendingFile = pendingUploads.coverLetter
                                      const hasExistingFile = id && applicationData?.candidateDetails?.coverLetter && !hasPendingFile && !removedFiles.coverLetter
                                      const filename = hasExistingFile
                                        ? applicationData?.candidateDetails?.coverLetter?.split("/")?.pop()
                                        : hasPendingFile
                                          ? pendingUploads.coverLetter.originalName
                                          : selectedCandidate?.coverLetter?.split("/")?.pop() || null

                                      return (
                                        <Flex align="center" gap={4}>
                                          <Box flex="1">
                                            <FileUpload
                                              isDisabled
                                              label={constant.CoverLetter}
                                              icon={AttachmentIcon}
                                              selectedFile={
                                                selectedCandidate?.coverLetter
                                                  ? `Selected: ${removeTimestamp(selectedCandidate.coverLetter.split("/").pop())}`
                                                  : "No file"
                                              }
                                              title={constant.attachment}
                                              inputId="cover-letter-upload"
                                              errorText={meta.error || ""}
                                              type="file"
                                              borderColor={
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor
                                              }
                                              accept="audio/*,video/*,image/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                            />
                                          </Box>
                                          {selectedCandidate?.coverLetter && (
                                            <Flex gap={2}>
                                              <Button
                                                size="sm"
                                                leftIcon={<DownloadIcon />}
                                                onClick={() => {
                                                  handleDownloadAttachment(selectedCandidate.coverLetter,
                                                    selectedCandidate.coverLetter.split("/").pop());
                                                }}
                                                variant="outline"
                                              >
                                                Download
                                              </Button>
                                            </Flex>
                                          )}
                                        </Flex>
                                      );
                                    }}
                                  </Field>
                                </Box>
                              </Flex>
                            </Box>

                            <Box className="createJobOpening-tabsPanelFooter">
                              <Button onClick={handleBack} className="backBtn">{constant.back}</Button>
                              {!isOverviewMode && (
                                <Button
                                  disabled={(!selectedCandidate?._id && !dirty) || !isValid || !permission?.update || (isEditMode && !isDirty)}
                                  className="nextBtn"
                                  variant="primary"
                                  type="submit"
                                >
                                  {editLoading ? <Spinner size="sm" /> : id ? constant.update : constant.save}
                                </Button>
                              )}
                              {isOverviewMode && (
                                <Button onClick={handleBackNavigation} className="nextBtn" variant="primary" type="button">
                                  Return to Applications
                                </Button>
                              )}
                            </Box>
                          </TabPanel>
                        );
                      } else if (originalIndex === 2) {
                        // Document Verification Tab
                        return (
                          <TabPanel key={`tab-${originalIndex}`} className="createJobOpening-tabsPanel">
                            <Box className="createJobOpening-tabsPanelBody">
                              {!isDocAndLOITabsEnabled ? (
                                <Box textAlign="center" py={10}>
                                  <Text color="gray.500" fontSize="lg">
                                    Document Verification is only available when application status is
                                    <Text as="span" fontWeight="bold"> Interview Cleared</Text> or later stages.
                                  </Text>
                                </Box>
                              ) : (
                                <DocumentVerificationTab
                                  candidateId={selectedCandidate?._id ? String(selectedCandidate._id) : undefined}
                                  candidateName={selectedCandidate?.name}
                                  applicationId={id}
                                  isReadOnly={isOverviewMode}
                                />
                              )}
                            </Box>
                          </TabPanel>
                        );
                      } else if (originalIndex === 3) {
                        // LOI Tab
                        return (
                          <TabPanel key={`tab-${originalIndex}`} className="createJobOpening-tabsPanel">
                            <Box className="createJobOpening-tabsPanelBody">
                              {!isDocAndLOITabsEnabled ? (
                                <Box textAlign="center" py={10}>
                                  <Text color="gray.500" fontSize="lg">
                                    LOI is only available when application status is
                                    <Text as="span" fontWeight="bold"> Interview Cleared</Text> or later stages.
                                  </Text>
                                </Box>
                              ) : (
                                <LOITab
                                  candidateId={selectedCandidate?._id ? String(selectedCandidate._id) : undefined}
                                  candidateName={selectedCandidate?.name}
                                  candidateEmail={selectedCandidate?.email}
                                  applicationId={id}
                                  isReadOnly={isOverviewMode}
                                />
                              )}
                            </Box>
                          </TabPanel>
                        );
                      }
                      return null;
                    })}
                  </TabPanels>
                </Tabs>
              </Form>
            )
          }}
        </Formik>
      </Box>
    </>
  )
}


const ApplicationCreate = () => {
  const colors = globalStyles.colors
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [highestTabReached, setHighestTabReached] = useState(0)
  const { id } = useParams()
  const [jobData, setJobData] = useState(null)
  const [candidateData, setCandidateData] = useState(null)
  const [selectedCandidate, setSelectedCandidate] = useState({})
  const [selectedJob, setSelectedJob] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const { addLoading } = useSelector((state) => state.candidates)
  const [editLoading, setEditLoading] = useState(false)
  const [applicationData, setApplicationData] = useState(null)
  const [permission] = HasAccess([accessModuleTitles.Applications]);
  const [isOverviewMode, setIsOverviewMode] = useState(false)
  const location = useLocation();
  const { mode } = location.state || {};
  const [activeMainTab, setActiveMainTab] = useState(0);

  // Get current user role to filter tabs (same as CandidatesCreate)
  const getUserRole = useMemo(() => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;
      const user = JSON.parse(userData);
      return user?.activeRoleId?.roleName || user?.role || null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }, []);

  // Filter tabs based on user role (same as CandidatesCreate)
  const visibleTabs = useMemo(() => {
    const userRole = getUserRole;
    if (userRole === 'employee') {
      // Hide Document Verification (index 2) and LOI (index 3) for employees
      return applicationTabsData.filter((_, index) => index < 2);
    }
    if (userRole === 'reviewer') {
      // For reviewers, only show Document Verification tab (index 2)
      return applicationTabsData.filter((_, index) => index === 2);
    }
    // For all other roles (recruiter, hiringManager, superAdmin, etc.), show all tabs
    return applicationTabsData;
  }, [getUserRole]);

  const handleMainTabChange = (index) => {
    setActiveMainTab(index);
  };

  const [removedFiles, setRemovedFiles] = useState({
    resume: false,
    coverLetter: false
  });
  const [originalApplicationData, setOriginalApplicationData] = useState(null);

  const [pendingUploads, setPendingUploads] = useState({
    resume: null,
    coverLetter: null
  })

  const s3HelperInstance = S3Helper.getInstance();

  // Get mode from URL query params or sessionStorage
  const getModeFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMode = urlParams.get('mode');
    if (urlMode) {
      sessionStorage.setItem("mode", urlMode);
      return urlMode;
    }

    if (window.location.hash) {
      const hashParts = window.location.hash.split("?");
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        const hashMode = hashParams.get('mode');
        if (hashMode) {
          sessionStorage.setItem("mode", hashMode);
          return hashMode;
        }
      }
    }

    return sessionStorage.getItem("mode");
  };

  const modeType = getModeFromUrl();
  const isEditMode = useMemo(() => {
    return modeType === "edit"
  }, [modeType])

  useEffect(() => {
    const mode = getModeFromUrl();
    if (mode === "overview") {
      setIsOverviewMode(true);
    } else {
      setIsOverviewMode(false);
    }
  }, []);

  // Handle back navigation based on where user came from
  const handleBackNavigation = useCallback(() => {
    // Check if there's a 'from' state indicating where user came from
    const fromSource = location.state?.from;
    const fromTab = location.state?.activeTab;
    const jobOpeningId = location.state?.jobOpeningId;
    const candidateId = location.state?.candidateId;

    if (fromSource === 'job-opening-additional-info' && jobOpeningId) {
      // Navigate back to job opening's Additional Info tab
      sessionStorage.setItem('activeMainTab', '1'); // Set to Additional Info tab
      navigate(`/recruitment/job-opening-create/${jobOpeningId}`, { 
        state: { activeMainTab: 1 } 
      });
    } else if (fromSource === 'candidate-additional-info' && candidateId) {
      navigate(`/recruitment/candidate-create/${candidateId}`, {
        state: { activeMainTab: 1 }
      });
    } else if (fromSource === 'document-verification') {
      // Navigate back to document verification list/entries
      navigate('/recruitment/document-verification');
    } else if (fromSource === 'loi') {
      // Navigate back to LOI list/entries
      navigate('/recruitment/loi');
    } else if (fromSource === 'onboarding-checklist') {
      // Navigate back to onboarding checklist
      navigate('/recruitment/onboarding-checklist');
    } else {
      // Default: navigate to applications list
      navigate('/recruitment/applications');
    }
  }, [navigate, location.state]);

  // Define validation schemas for each tab
  const tabValidationSchemas = useMemo(
    () => ({
      0: Yup.object().shape({
        postingTitle: Yup.string().required(constant.requiredJob),
        name: Yup.string().required(constant.requiredName),
        status: Yup.string().required(constant.requiredStatus),
        remarks: Yup.string().nullable().max(1000, "Remarks cannot exceed 1000 characters"),
      }),
      1: Yup.object().shape({
        resume: Yup.string().nullable(),
        coverLetter: Yup.string().nullable(),
      }),
    }),
    [],
  );

  // Function to check if a specific tab is valid
  const isTabValid = useCallback(
    (values, tabIndex) => {
      try {
        if (!tabValidationSchemas[tabIndex]) {
          return true;
        }
        tabValidationSchemas[tabIndex].validateSync(values, {
          abortEarly: false,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    [tabValidationSchemas, id, applicationData]
  );

  // Function to get tab navigation states
  const getTabStates = useCallback(
    (values, dirty, isValid, currentTabIndex) => {
      const tabStates = {}
      const totalTabs = applicationTabsData.length;

      let firstInvalidTab = null
      for (let i = 0; i <= Math.min(highestTabReached, 1); i++) {
        if (!isTabValid(values, i)) {
          firstInvalidTab = i
          break
        }
      }

      for (let i = 0; i < totalTabs; i++) {
        // Special condition for tabs 2 and 3 - require specific application status
        const isStatusAllowed = ALLOWED_STATUSES_FOR_DOC_LOI.includes(values.status);

        if (i >= 2 && !isStatusAllowed) {
          tabStates[i] = { isAccessible: false, isValid: false, canProceed: false };
          continue;
        }

        if (i < currentTabIndex) {
          if (i <= 1) {
            tabStates[i] = { isAccessible: true, isValid: isTabValid(values, i) }
          } else {
            tabStates[i] = { isAccessible: true, isValid: true }
          }
        } else if (i === currentTabIndex) {
          if (i <= 1) {
            const currentTabValid = isTabValid(values, i)
            tabStates[i] = {
              isAccessible: true,
              isValid: currentTabValid,
              canProceed: currentTabValid,
            }
          } else {
            tabStates[i] = {
              isAccessible: true,
              isValid: true,
              canProceed: true,
            }
          }
        } else if (firstInvalidTab !== null && i > firstInvalidTab && i <= 1) {
          tabStates[i] = { isAccessible: false, isValid: false, canProceed: false }
        } else if (i <= 1 && i <= highestTabReached && isTabValid(values, currentTabIndex)) {
          tabStates[i] = { isAccessible: true, isValid: false, canProceed: false }
        } else if (i <= 1 && i === currentTabIndex + 1 && isTabValid(values, currentTabIndex)) {
          tabStates[i] = { isAccessible: true, isValid: false, canProceed: false }
        } else if (i > 1 && isStatusAllowed) {
          if (firstInvalidTab === null || currentTabIndex >= 1) {
            tabStates[i] = { isAccessible: true, isValid: true, canProceed: true }
          } else {
            tabStates[i] = { isAccessible: false, isValid: false, canProceed: false }
          }
        } else {
          tabStates[i] = { isAccessible: false, isValid: false, canProceed: false }
        }
      }

      return tabStates
    },
    [isTabValid, highestTabReached],
  )

  const handleTabChange = useCallback(
    (index, values, dirty, isValid) => {
      if (index < activeTabIndex) {
        setActiveTabIndex(index)
        return
      }

      if (index === activeTabIndex) {
        return
      }

      const tabStates = getTabStates(values, dirty, isValid, activeTabIndex);

      if (tabStates[index]?.isAccessible) {
        setActiveTabIndex(index);
        if (index > highestTabReached) {
          setHighestTabReached(index);
        }
      } else {
        toast.error("Please complete the current step before proceeding")
      }
    },
    [activeTabIndex, getTabStates, highestTabReached],
  )

  const handleNext = useCallback(
    (values, dirty, isValid, setErrors, setTouched) => {
      if (activeTabIndex < 3) {
        const currentTabValid = isTabValid(values, activeTabIndex)

        if (currentTabValid) {
          const newTabIndex = activeTabIndex + 1

          // Check if trying to access restricted tabs (2 or 3) without allowed status
          const isStatusAllowed = ALLOWED_STATUSES_FOR_DOC_LOI.includes(values.status);

          if (newTabIndex >= 2 && !isStatusAllowed) {
            toast.error("Document Verification and LOI sections are only available when application status is 'Interview Cleared' or later stages");
            return;
          }

          setActiveTabIndex(newTabIndex)
          if (newTabIndex > highestTabReached) {
            setHighestTabReached(newTabIndex)
          }
        } else {
          try {
            if (tabValidationSchemas[activeTabIndex]) {
              tabValidationSchemas[activeTabIndex].validateSync(values, { abortEarly: false })
            }
          } catch (error) {
            const errors = {}
            const touchedFields = {}
            error.inner.forEach((err) => {
              errors[err.path] = err.message
              touchedFields[err.path] = true
            })
            setErrors(errors)
            setTouched(touchedFields)
          }
          toast.error("Please fill all required fields before proceeding")
        }
      }
    },
    [activeTabIndex, isTabValid, tabValidationSchemas, highestTabReached],
  )

  const handleBack = useCallback(() => {
    if (activeTabIndex > 0) {
      setActiveTabIndex(activeTabIndex - 1)
    }
  }, [activeTabIndex])

  useEffect(() => {
    if (id && applicationData) {
      setHighestTabReached(1)
    }
  }, [id, applicationData])

  const fetchJobs = async () => {
    setIsLoading(true)
    try {
      const result = await getApi("api/recruitment/jobOpening/?allData=true")
      if (result?.data?.data) {
        setJobData(result?.data?.data)
      } else {
        toast.error(result?.response?.data?.message || recruitConstant.errorMsg);
      }
    } catch (e) {
      toast.error(recruitConstant.errorMsg)
      console.error("Error fetching job openings:", e)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCandidates = async () => {
    setIsLoading(true)
    try {
      const result = await getApi("api/recruitment/candidates/?allData=true")
      if (result?.data?.data) {
        setCandidateData(result?.data?.data)
      } else {
        toast.error(result?.response?.data?.message || recruitConstant.errorMsg);
      }
    } catch (e) {
      toast.error(recruitConstant.errorMsg)
      console.error("Error fetching candidates:", e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
    fetchCandidates()
  }, [])

  const EditData = async (values) => {
    try {
      setEditLoading(true)
      const response = await putApi(`${API_PATHS.RECRUITMENT_APPLICATION_UPDATE}${id}`, values)
      if (response.status === 200) {
        toast.success("Application updated successfully")
        handleBackNavigation() // Use context-aware navigation instead of hardcoded path
      } else {
        toast.error(response?.response?.data?.message || constant.somethingWrong);
      }
    } catch (e) {
      toast.error(constant.somethingWrong)
    } finally {
      setEditLoading(false)
    }
  }

  const handleDownloadWithS3Helper = async (fileUrl, fileName) => {
    try {
      let user = null;
      const userStr = localStorage.getItem('user');
      if (userStr) {
        user = JSON.parse(userStr);
      }
      let downloadUrl = fileUrl;
      const data = [{
        fileName: fileName,
        method: "GET",
        filePath: fileUrl.replace(fileName, ''),
        userId: user?._id || 'anonymous'
      }]

      if (!fileUrl.startsWith('http')) {
        const urlData = await s3HelperInstance.getDownloadUrl(data);
        downloadUrl = urlData.url;
      }

      const response = await axios.get(downloadUrl, { responseType: 'blob' });

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = removeTimestamp(fileName) || 'download';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      toast.success('File downloaded successfully')
    } catch (error) {
      console.error('=== DOWNLOAD ERROR ===', error)
      toast.error('Failed to download file')
    }
  }

  const handleDownloadAttachment = async (attachmentUrl, filename) => {
    try {
      await handleDownloadWithS3Helper(attachmentUrl, filename);
    } catch (error) {
      console.error("Download attachment error:", error);
      try {
        await dispatch(downloadAttachment({ attachmentUrl, filename }));
      } catch (fallbackError) {
        console.error("Fallback download also failed:", fallbackError);
      }
    }
  };

  useEffect(() => {
    if (id) {
      const fetchTaskData = async () => {
        try {
          const result = await getApi(API_PATHS.RECRUITMENT_APPLICATION, id)
          setSelectedCandidate(result?.data?.candidate)
          setSelectedJob(result?.data?.job)
          setApplicationData(result?.data?.application)
          const updatedRes = {
            postingTitle: postingTitleValue(result?.data?.application),
            name: result?.data?.application?.candidateDetails?._id,
            email: result?.data?.application?.candidateDetails?.email,
            phone: result?.data?.application?.candidateDetails?.phone,
            city: result?.data?.application?.candidateDetails?.city,
            experienceYears: result?.data?.application?.candidateDetails?.experienceYears,
            state: result?.data?.application?.candidateDetails?.state,
            noticePeriod: result?.data?.application?.candidateDetails?.noticePeriod,
            status: result?.data?.application?.status,
            remarks: result?.data?.application?.remarks || "",
            applicationSource: result?.data?.application?.candidateDetails?.source,
            resume: result?.data?.application?.candidateDetails?.resume,
            coverLetter: result?.data?.application?.candidateDetails?.coverLetter,
          }
          setOriginalApplicationData(updatedRes)
        } catch (e) {
          toast.error(constant.somethingWrong)
        }
      }

      fetchTaskData()
    }
  }, [id])

  // Handle redirect to specific tab from navigation state
  useEffect(() => {
    if (applicationData && location.state?.activeTab !== undefined) {
      const targetTab = location.state.activeTab;

      if (targetTab >= 2) {
        if (ALLOWED_STATUSES_FOR_DOC_LOI.includes(applicationData.status)) {
          setActiveTabIndex(targetTab);
          setHighestTabReached((prev) => Math.max(prev, targetTab));
        }
      } else {
        setActiveTabIndex(targetTab);
        setHighestTabReached((prev) => Math.max(prev, targetTab));
      }
    }
  }, [applicationData, location.state]);

  // Set default tab to Document Verification for reviewers
  useEffect(() => {
    // Only set default tab if no explicit tab was requested from navigation state
    if (applicationData && location.state?.activeTab === undefined) {
      const userRole = getUserRole;
      
      // If user is a reviewer and the application status allows doc/LOI tabs
      if (userRole === 'reviewer' && ALLOWED_STATUSES_FOR_DOC_LOI.includes(applicationData.status)) {
        // Set to Document Verification tab (index 2)
        setActiveTabIndex(2);
        setHighestTabReached((prev) => Math.max(prev, 2));
      }
    }
  }, [applicationData, location.state, getUserRole]);

  const postingTitleValue = (passApplicationData) => {
    const data = passApplicationData ? passApplicationData : applicationData
    if (data?.job?.postingTitle && data?.job?.customId) {
      const formattedTitle = data?.job?.postingTitle
        ?.split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
      const jobId = data?.job?.customId
      return `${formattedTitle} - ${jobId}`
    }
  }

  return (
    <div>
      <ActionNavbar isCreatePage={true} />
      <Stack>
        {id ? (
          <Tabs index={activeMainTab} onChange={handleMainTabChange}>
            <TabList className="noRadiusTabs">
              <Tab>Overview</Tab>
              <Tab>Additional Info</Tab>
            </TabList>

            <TabPanels mt={2}>
              <TabPanel>
                <OverviewPanel
                  navigate={navigate}
                  isOverviewMode={isOverviewMode}
                  id={id}
                  applicationData={applicationData}
                  handleDownloadAttachment={handleDownloadAttachment}
                  pendingUploads={pendingUploads}
                  removedFiles={removedFiles}
                  permission={permission}
                  colors={colors}
                  activeTabIndex={activeTabIndex}
                  getTabStates={getTabStates}
                  setActiveTabIndex={setActiveTabIndex}
                  handleTabChange={handleTabChange}
                  handleNext={handleNext}
                  handleBack={handleBack}
                  editLoading={editLoading}
                  isEditMode={isEditMode}
                  originalApplicationData={originalApplicationData}
                  jobData={jobData}
                  candidateData={candidateData}
                  postingTitleValue={postingTitleValue}
                  selectedCandidate={selectedCandidate}
                  selectedJob={selectedJob}
                  EditData={EditData}
                  dispatch={dispatch}
                  setSelectedCandidate={setSelectedCandidate}
                  setSelectedJob={setSelectedJob}
                  visibleTabs={visibleTabs}
                  handleBackNavigation={handleBackNavigation}
                />
              </TabPanel>

              <TabPanel display={'flex'} flexDirection={'column'} gap={6}>
                <Box className="createJobOpening-breadCrumb">
                  <ArrowBackIcon onClick={handleBackNavigation} fontSize="20px" cursor={"pointer"} />
                  <Text className="createJobOpening-pageTitle">
                    {constant.additionalInfo}
                  </Text>
                </Box>
                <Box>
                  <Box width={'100%'} padding={4} borderBottom={"1px"} borderColor={'#e9ebec'} borderTopRadius={"xl"} backgroundColor={'white'}>
                    <Text fontWeight={700} fontSize={16}>Interview</Text>
                  </Box>
                  <Interview
                    customApiEndpoint={id ? `api/recruitment/application/${id}/interviews` : undefined}
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          <OverviewPanel
            navigate={navigate}
            isOverviewMode={isOverviewMode}
            id={id}
            applicationData={applicationData}
            handleDownloadAttachment={handleDownloadAttachment}
            pendingUploads={pendingUploads}
            removedFiles={removedFiles}
            permission={permission}
            colors={colors}
            activeTabIndex={activeTabIndex}
            getTabStates={getTabStates}
            setActiveTabIndex={setActiveTabIndex}
            handleTabChange={handleTabChange}
            handleNext={handleNext}
            handleBack={handleBack}
            editLoading={editLoading}
            isEditMode={isEditMode}
            originalApplicationData={originalApplicationData}
            jobData={jobData}
            candidateData={candidateData}
            postingTitleValue={postingTitleValue}
            selectedCandidate={selectedCandidate}
            selectedJob={selectedJob}
            EditData={EditData}
            dispatch={dispatch}
            setSelectedCandidate={setSelectedCandidate}
            setSelectedJob={setSelectedJob}
            visibleTabs={visibleTabs}
            handleBackNavigation={handleBackNavigation}
          />
        )}
      </Stack>
    </div>
  )
}

export default ApplicationCreate
